import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { formatTimeBucket } from "./helpers";

/** line + area — area only differs by the per-series `areaStyle` fill. */
export function buildLineAreaOption({ viz, query, data, palette, base, measure, xKey }: ChartBuildCtx): ChartOption {
  const yFmt = formatFromColumn(measure, data.columns);
  // Integer-count measures with small ranges (< 10) get integer
  // axis ticks via minInterval=1 so we don't show "0, 0.5, 1, 1.5".
  const lineValues = data.rows.flatMap((r) => query.measures.map((m) => Number(r[m] ?? 0)));
  const lineMax = lineValues.length > 0 ? Math.max(...lineValues) : 0;
  const lineUseIntTicks = (yFmt === "number" || !yFmt) && lineMax > 0 && lineMax < 10;
  return {
    ...base,
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: unknown) => formatValue(v, yFmt),
    },
    legend: {
      // Show whenever there's > 1 measure OR a comparison overlay
      // exists; otherwise the dashed "(prev)" series goes anonymous.
      show: query.measures.length > 1 || (data.comparisonRows && data.comparisonRows.length > 0),
      bottom: 0,
      textStyle: { color: "#6b7280", fontSize: 11 },
      // Truncate long measure names so they don't push the legend
      // off-screen when there are 4+ measures.
      formatter: (n: string) => (n.length > 22 ? n.slice(0, 21) + "…" : n),
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: data.rows.map((r) => r[xKey] as string),
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        // ISO datetime strings from cube overflow the axis at every
        // tick, so ECharts auto-hides them (eval-line PNG showed a
        // blank x-axis). Format through formatTimeBucket using the
        // query's granularity for compact "Apr '26" / "May 18" labels.
        formatter: (v: string) => formatTimeBucket(v, query.timeDimension?.granularity),
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        // Force integer ticks when format is "number" — auto-scale
        // otherwise lands on fractional ticks like "220.5" for a
        // count of 213, which reads as broken (caught in
        // eval-line-profiles-shared-weekly).
        formatter: (v: number) =>
          !yFmt || yFmt === "number" ? formatValue(Math.round(v), yFmt) : formatValue(v, yFmt),
      },
      splitLine: { lineStyle: { color: "rgba(17,24,39,0.06)" } },
      // Reserve ~8% headroom above the data max so the "top-of-chart"
      // point label doesn't clip against the chart border. ECharts'
      // default auto-scale lands the max exactly at the gridline,
      // which leaves no room for the value label drawn at position:top.
      max: (value: { max: number; min: number }) => {
        const span = value.max - value.min || Math.abs(value.max) || 1;
        return value.max + span * 0.08;
      },
      // Integer-count measures with small ranges: pin tick interval
      // to 1 so the axis reads as 0, 1, 2, 3 rather than 0, 0.5, 1…
      // For larger count measures rely on the rounded formatter above.
      ...(lineUseIntTicks ? { minInterval: 1 } : (!yFmt || yFmt === "number" ? { minInterval: 1 } : {})),
    },
    grid: { top: 24, right: 16, bottom: 28, left: 56, containLabel: true },
    series: [
      ...query.measures.map((m, idx) => {
        const mFmt = formatFromColumn(m, data.columns);
        return {
          name: humanizeMeasure(m),
          type: "line" as const,
          data: data.rows.map((r) => Number(r[m] ?? 0)),
          smooth: true,
          symbol: "circle",
          symbolSize: 5,
          lineStyle: { width: 2 },
          areaStyle: viz.type === "area" ? { opacity: 0.18 } : undefined,
          color: palette[idx % palette.length],
          // Print-readable: label every Nth point so the chart says what
          // the values are without a hover. Density-aware so we don't
          // litter labels on a busy series.
          label: {
            show: true,
            position: "top",
            fontSize: 10,
            color: "#4b5563",
            distance: 6,
            formatter: (p: { dataIndex: number; value: number }) => {
              const n = data.rows.length;
              const stride = n <= 8 ? 1 : n <= 20 ? Math.ceil(n / 8) : Math.ceil(n / 6);
              const isLast = p.dataIndex === n - 1;
              if (!isLast && p.dataIndex % stride !== 0) return "";
              return formatValue(p.value, mFmt);
            },
          },
        };
      }),
      // ── Comparison-period overlay (dashed, no labels, no area fill).
      // Shown bucket-for-bucket on the same x axis so the user reads
      // "we're tracking N% ahead/behind day-for-day last period".
      ...(data.comparisonRows && data.comparisonRows.length > 0
        ? query.measures.map((m, idx) => ({
            name: `${humanizeMeasure(m)} (prev)`,
            type: "line" as const,
            data: (data.comparisonRows ?? []).map((r) => Number(r[m] ?? 0)),
            smooth: true,
            symbol: "none" as const,
            lineStyle: { width: 1.5, type: "dashed" as const, opacity: 0.65 },
            color: palette[idx % palette.length],
            label: { show: false },
            tooltip: { show: true },
            z: 1,
          }))
        : []),
    ],
  } as ChartOption;
}
